import { Intersection, Line, Point } from "types/canvasTypes";

function dotProduct(point1: Point, point2: Point): number {
  return point1.x * point2.x + point1.y * point2.y;
}

function magnitude(point: Point): number {
  return Math.sqrt(point.x * point.x + point.y * point.y);
}

function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

function calculateAngle(line1: Line, line2: Line): number {
  const vector1 = {
    x: line1.end.x - line1.start.x,
    y: line1.end.y - line1.start.y,
  };
  const vector2 = {
    x: line2.end.x - line2.start.x,
    y: line2.end.y - line2.start.y,
  };

  const dot = dotProduct(vector1, vector2);
  const magnitude1 = magnitude(vector1);
  const magnitude2 = magnitude(vector2);
  const cosTheta = dot / (magnitude1 * magnitude2);

  return toDegrees(Math.acos(cosTheta));
}

function pointsAreAlmostEqual(
  point1: Point,
  point2: Point,
  threshold: number
): boolean {
  return (
    Math.abs(point1.x - point2.x) < threshold &&
    Math.abs(point1.y - point2.y) < threshold
  );
}

export type GlassAngle = {
  internal: boolean;
  angle: number;
};

export function findLinesWithSharedPoints(
  line: Line,
  lines: Line[],
  threshold: number = 5
): Line[] {
  const result: Line[] = [];

  for (const otherLine of lines) {
    if (line.start === otherLine.start && line.end === otherLine.end) {
      continue;
    }

    if (
      pointsAreAlmostEqual(line.start, otherLine.start, threshold) ||
      pointsAreAlmostEqual(line.start, otherLine.end, threshold) ||
      pointsAreAlmostEqual(line.end, otherLine.start, threshold) ||
      pointsAreAlmostEqual(line.end, otherLine.end, threshold)
    ) {
      result.push(otherLine);
    }
  }

  return result;
}

export function getAllIntersections(
  lines: Line[],
  pointEqualityThreshold: number = 5
): Intersection[] {
  const intersections: Intersection[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    intersections.push({
      line,
      intersections: findLinesWithSharedPoints(
        line,
        lines,
        pointEqualityThreshold
      ),
    });
  }

  return intersections;
}

export function checkLineIntersection(line1: Line, line2: Line): GlassAngle {
  const x1 = line1.start.x;
  const y1 = line1.start.y;
  const x2 = line1.end.x;
  const y2 = line1.end.y;
  const x3 = line2.start.x;
  const y3 = line2.start.y;
  const x4 = line2.end.x;
  const y4 = line2.end.y;

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  if (denominator === 0) {
    return { internal: false, angle: 0 };
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
    const angle = calculateAngle(line1, line2);
    if (angle > 90 || angle < 90) {
      return { internal: true, angle: 180 - angle };
    }
    return { internal: true, angle };
  }

  return { internal: false, angle: 180 - calculateAngle(line1, line2) };
}
